import { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import { Toaster } from "sonner";
import { ShieldBan } from "lucide-react";
import { Header } from "./components/site/Header";
import { Hero } from "./components/site/Hero";
import { Exchange } from "./components/site/Exchange";
import { Games } from "./components/site/Games";
import { HowItWorks } from "./components/site/HowItWorks";
import { Payments } from "./components/site/Payments";
import { Faq } from "./components/site/Faq";
import { Footer } from "./components/site/Footer";
import { IdModal, FloatingContact } from "./components/site/Contact";
import { Testimonials } from "./components/site/Testimonials";
import { PromoBanner } from "./components/site/PromoBanner";
import { WinnerPopup } from "./components/site/WinnerPopup";
import { Exchanges } from "./components/site/Exchanges";
import { Proofs } from "./components/site/Proofs";
import { StatsBar, BetTypes, Fixtures } from "./components/site/MoreSections";
import { Cities, Rating, Guides } from "./components/site/MoreSections2";
import { ExchangesAdmin, ProofsAdmin } from "./admin/ExchangesProofs";
import { LangProvider, useLang } from "./i18n/LangContext";
import { SiteProvider, useSite } from "./context/SiteContext";
import { setHead, parseSchema } from "./lib/head";
import BlogList from "./pages/BlogList";
import PostView from "./pages/PostView";
import AdminLogin from "./admin/AdminLogin";
import AdminLayout from "./admin/AdminLayout";
import Dashboard from "./admin/Dashboard";
import ContentEditor from "./admin/ContentEditor";
import PostsList from "./admin/PostsList";
import PostEditor from "./admin/PostEditor";
import SettingsPage from "./admin/SettingsPage";
import Security from "./admin/Security";
import Health from "./admin/Health";

const useHomeSeo = () => {
  const { t, lang } = useLang();
  const { site } = useSite();
  useEffect(() => {
    const hs = site?.home_seo || {};
    const faq = { "@context": "https://schema.org", "@type": "FAQPage", mainEntity: t.faq.items.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) };
    setHead({ title: t.seo.title, description: t.seo.description, canonical: `${window.location.origin}/`, image: hs.og_image || undefined,
      locale: lang === "hi" ? "hi_IN" : "en_IN", schema: [faq, ...parseSchema(hs.schema_json)] });
  }, [t, lang, site]);
};

function Home({ onJoin }) {
  useHomeSeo();
  const s = useSite().site?.content?.sections || {};
  const on = (k) => s[k] !== false;
  return (
    <main>
      {on("hero") && <Hero onJoin={onJoin} />}
      {on("promo") && <PromoBanner onJoin={onJoin} />}
      {on("statsbar") && <StatsBar />}
      {on("exch") && <Exchanges onJoin={onJoin} />}
      {on("exchange") && <Exchange onJoin={onJoin} />}
      {on("bets") && <BetTypes onJoin={onJoin} />}
      {on("games") && <Games onJoin={onJoin} />}
      {on("fixtures") && <Fixtures onJoin={onJoin} />}
      {on("how") && <HowItWorks />}
      {on("proofs") && <Proofs />}
      {on("cities") && <Cities />}
      {on("rating") && <Rating />}
      {on("testi") && <Testimonials />}
      {on("pay") && <Payments />}
      {on("guides") && <Guides />}
      {on("faq") && <Faq />}
    </main>
  );
}

const Blocked = () => (
  <div className="grid min-h-screen place-items-center bg-[#0A0A0A] px-6 text-center font-sans text-white" data-testid="blocked-screen">
    <div><ShieldBan className="mx-auto text-[#D4AF37]" size={40} /><h1 className="mt-4 font-serif text-2xl">Access restricted</h1><p className="mt-2 text-sm text-zinc-500">Your access to this website has been blocked.</p></div>
  </div>
);

function Shell({ home }) {
  const [modal, setModal] = useState(false);
  const { lang } = useLang();
  const { site, blocked } = useSite();
  const fx = { particles: true, glow: true, countup: true, ...(site?.effects || {}) };
  const onJoin = () => setModal(true);
  if (blocked) return <Blocked />;
  return (
    <div className={`App min-h-screen bg-[#0A0A0A] font-sans text-white lang-${lang} ${fx.glow ? "fx-glow" : ""}`}>
      <Header onJoin={onJoin} />
      {home ? <Home onJoin={onJoin} /> : <main><Outlet /></main>}
      <Footer onJoin={onJoin} />
      <IdModal open={modal} onOpenChange={setModal} />
      <FloatingContact />
      {site?.content?.sections?.winner !== false && <WinnerPopup />}
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <SiteProvider>
        <LangProvider>
          <Toaster theme="dark" position="top-center" richColors />
          <Routes>
            <Route path="/" element={<Shell home />} />
            <Route element={<Shell />}>
              <Route path="/blog" element={<BlogList />} />
              <Route path="/blog/:slug" element={<PostView kind="blog" />} />
              <Route path="/p/:slug" element={<PostView kind="page" />} />
            </Route>
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminLayout />}>
              <Route index element={<Dashboard />} />
              <Route path="content" element={<ContentEditor />} />
              <Route path="exchanges" element={<ExchangesAdmin />} />
              <Route path="proofs" element={<ProofsAdmin />} />
              <Route path="blogs" element={<PostsList kind="blog" />} />
              <Route path="blogs/:id" element={<PostEditor kind="blog" />} />
              <Route path="pages" element={<PostsList kind="page" />} />
              <Route path="pages/:id" element={<PostEditor kind="page" />} />
              <Route path="settings" element={<SettingsPage />} />
              <Route path="security" element={<Security />} />
              <Route path="health" element={<Health />} />
            </Route>
          </Routes>
        </LangProvider>
      </SiteProvider>
    </BrowserRouter>
  );
}

export default App;
